import {
  ADD_INTERACTION_SUCCESS,
  GET_INTERACTIONS_SUCCESS,
  GET_INTERACTION_SUCCESS,
  UPDATE_NOTES_SUCCESS,
} from 'actions';
import { InteractionType } from 'enums/interactionType';
import { RoleType } from 'enums/roleType';
import { Maybe, SentimentEmotion, SentimentScore } from 'graph/types';
import { Model, ModelType, attr, fk } from 'redux-orm';
import { getUnregisteredAttendees } from 'utils/interactions/attendees';
import { AttendeeInteraction } from 'utils/interactions/types';

export interface IInteraction {
  id: string;
  accountId: string;
  attendees: string[];
  attendingContacts: Array<{
    id: string;
    firstName: string;
    lastName: string;
    emails?: string[];
    phone?: string[];
    company?: {
      id: string;
      name: string;
    };
  }>;
  attendingUsers: Array<{
    id: string;
    emails: string[];
    phone: string[];
    firstName: string;
    lastName: string;
  }>;
  companyId: string;
  contactId: string;
  endTimestamp: string;
  interactionNote: string | null;
  interactionNoteUpdatedAt: string;
  interactionType: InteractionType;
  isArchived?: boolean;
  length: number;
  points: number;
  rating: number;
  // not sure about this review type.
  reviews: {
    userId?: string;
    id?: string;
    value: number;
    rating?: number;
  }[];
  roleType: RoleType;
  sentiment: {
    sentiment: SentimentEmotion;
    sentimentScore: SentimentScore;
  };
  sourceId: string;
  /**
   * Last updated timestamp
   */
  timestamp: string;
  title: string;
}

interface Action {
  type: string;
  id?: string;
  data?: Array<Maybe<IInteraction>>;
  interaction?: IInteraction;
  error?: string;
}

class Interaction extends Model<typeof Interaction, IInteraction> {
  attendingUsers!: Array<{
    id: string;
    emails: string[];
    phone: string[];
    firstName: string;
    lastName: string;
  }>;

  reviews!: {
    userId?: string;
    id?: string;
    value: number;
  }[];

  static reducer(action: Action, model: ModelType<Interaction>) {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case GET_INTERACTIONS_SUCCESS:
        (action.data || []).forEach((interaction) => {
          if (!interaction) {
            return;
          }
          const current = model.withId(interaction.id);
          if (!current) {
            model.upsert(interaction);
          } else if (!current.isArchived) {
            // Only update an existing interaction if it is not already archived
            model.upsert(interaction);
          }
        });
        break;

      case ADD_INTERACTION_SUCCESS:
      case GET_INTERACTION_SUCCESS:
      case UPDATE_NOTES_SUCCESS:
        if (action.interaction) {
          model.upsert(action.interaction);
        }
        break;
    }

    return undefined;
  }

  toViewModel() {
    return {
      ...this.ref,

      otherAttendees: getUnregisteredAttendees(
        this.ref as unknown as AttendeeInteraction,
      ),
      attendingUsers: this.attendingUsers || [],
      reviews: this.reviews || [],
    };
  }
}

Interaction.modelName = 'Interaction';

Interaction.fields = {
  id: attr(),
  accountId: attr(),
  contactId: fk('Contact'),
  attendees: attr(),
  attendingUsers: attr(),
  attendingContacts: attr(),
  timestamp: attr(),
  interactionType: attr(),
  interactionNote: attr(),
  interactionNoteUpdatedAt: attr(),
  isArchived: attr(),
  points: attr(),
  roleType: attr(),
  length: attr(),
  sentiment: attr(),
  title: attr(),
};

export type InteractionViewModel = ReturnType<Interaction['toViewModel']>;

export default Interaction;
